
import Vue from 'vue';
import rootConfig from '@root/config/root.config';
import { Config, DataComments } from '@root/modules/article/types/article';
import ArticleConfig from '@root/modules/article/config/article.config';
import ArticleForCommentsService from '@root/modules/article/services/ArticleForComments.service';
import AdsLayout from '@root/common/components/layout/AdsLayout.vue';
import LinkHandler from '@core/components/helpers/LinkHandler.vue';
import { loadScripts } from '@root/common/utils/scriptLoader';
import { AdFormOptions } from '@ads/types/AdForm';
import CommentsSkeletonLoader from '@root/modules/article/components/CommentsSkeletonLoader.vue';

import { getArticleMeta } from '@root/modules/article/utils/articleMeta';
import getArticleAdsSettings from '@root/modules/article/utils/getArticleAdsSettings';

import { gridConfig } from '@core/config/grid.config';

interface Data {
  article: DataComments | null;
  commentsAds: Config['commentsAds'];
  showComments: boolean;
  articleUrl: string;
  isMobileView: boolean;
}

interface Computed {
  commentsContainer: string;
  adsPlacements: Record<string, AdFormOptions> | null;
  adsAttributes: {
    mkv: Record<string, string>;
    mkw: string[];
  };
  disabledAdsList: string[];
  title: string;
}

interface Methods {
  loadComments: () => void;
  loadCommentsEmbed: () => void;
}

interface CommentsWindow extends Window {
  loginApiToken: string;
  napo: any;
}

declare let window: CommentsWindow;

export default Vue.extend<Data, Methods, Computed, unknown>({
  components: {
    AdsLayout,
    LinkHandler,
    CommentsSkeletonLoader,
  },
  validate({ params }) {
    return /^\d+$/.test(params.id) && /^[a-z0-9-]+$/.test(params.slug);
  },
  async fetch() {
    const articleId = this.$route.params.id;
    const { lang } = this.$channelConfig('settings');

    try {
      const variables = {
        id: articleId,
        authorLanguage: lang,
      };

      const articleForCommentsService = new ArticleForCommentsService({ req: this.$ssrContext?.req });
      const data = await articleForCommentsService.fetch(variables);

      if (!data?.article) {
        this.$nuxt.error({ statusCode: 404, message: 'error.page_not_found' });
        return;
      }

      this.$store.commit('article/setActiveArticle', data.article);

      this.$store.commit(
        'adForm/setPageAdsSettings',
        getArticleAdsSettings({ $channelConfig: this.$channelConfig, $route: this.$route, article: data.article })
      );

      this.article = data.article;

      this.articleUrl = `${this.$channelConfig('meta').base.baseUrl}/a/${articleId}`;
    } catch (e) {
      this.$sentry.captureException(`Fetch article for comments failed: ${e}`, { tags: { 'process.server': process.server ? 'server' : 'client' } });
      this.$nuxt.error(e);
    }
  },
  data() {
    return {
      article: null,
      commentsAds: ArticleConfig.commentsAds,
      showComments: false,
      articleUrl: '',
      isMobileView: false,
    };
  },
  computed: {
    commentsContainer() {
      return `comments-fe-${this.$route.params.id}`;
    },
    adsPlacements() {
      return this.$store.getters['adForm/getAdsPlacements'];
    },
    adsAttributes() {
      const keywords: string[] = this.$store.getters['adForm/getAdsKeywords'];
      const keyValues: Record<string, string> = this.$store.getters['adForm/getAdsKeyValues'];

      return {
        mkw: keywords,
        mkv: keyValues,
      };
    },
    disabledAdsList() {
      return this.$store.getters['adForm/getDisabledAdsList'];
    },
    title() {
      if (!this.article) {
        return '';
      }

      return this.article.content.title.html.replace(/(<\/?h1>)/gi, '');
    },
  },
  mounted() {
    if (process.client) {
      const screenWidth = window.screen.width;
      const isIpad = (/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1) as boolean;
      this.isMobileView = screenWidth <= gridConfig.breakpoints.lg || isIpad;

      this.$nextTick(() => {
        this.loadComments();
      });
    } else {
      this.isMobileView = this.$ua && (this.$ua.isFromSmartphone() || this.$ua.isFromMobilephone() || this.$ua.isFromTablet());
    }
  },
  beforeDestroy() {
    this.$store.commit('article/setActiveArticle', null);
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('adForm/resetPageAdsSettings');
    next();
  },
  methods: {
    loadComments() {
      // Do not load napo script if it is already loaded
      if (!window.napo) {
        loadScripts(rootConfig.napo_url).then(() => {
          this.loadCommentsEmbed();
        });
      } else {
        this.loadCommentsEmbed();
      }
    },
    loadCommentsEmbed() {
      window.napo.push(['_embedFull', this.commentsContainer, this.$route.params.id, this.articleUrl]);
      this.showComments = true;
    },
  },
  head() {
    const meta = getArticleMeta({ $channelConfig: this.$channelConfig, article: this.article, path: this.$route.path });
    meta.url = this.$route.path;
    meta.type = 'website';

    let data = [...meta.data];
    data?.push({ hid: 'cXenseParse:eks-taxonomy', name: 'cXenseParse:eks-taxonomy', content: 'comments' });
    data?.push({ hid: 'cXenseParse:url', name: 'cXenseParse:url', content: `${this.$channelConfig('meta').base.baseUrl}${this.articleUrl}` });

    // Override meta.robots default value injected in Meta.ts with 'noindex'
    data = data.filter((item) => item.hid !== 'robots');
    data?.push({ hid: 'robots', name: 'robots', content: 'noindex' });

    return {
      title: meta.title,
      meta: data,
      style: meta.style,
      link: meta.link,
      __dangerouslyDisableSanitizers: ['style'],
    };
  },
});
